if (!customElements.get('ncf-site-header')) {
  customElements.define(
    'ncf-site-header',
    class NcfSiteHeader extends HTMLElement {
      connectedCallback() {
        this.triggers = Array.from(this.querySelectorAll('[data-open-panel]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));
        this.closers = Array.from(this.querySelectorAll('[data-close-panels]'));
        this.toggle = this.querySelector('[data-menu-toggle]');
        this.drawer = this.querySelector('[data-mobile-drawer]');
        this.sheet = this.drawer ? this.drawer.querySelector('[role="dialog"]') : null;
        this.drawerClosers = this.drawer ? Array.from(this.drawer.querySelectorAll('[data-drawer-close]')) : [];
        this.accordions = this.drawer ? Array.from(this.drawer.querySelectorAll('[data-accordion]')) : [];
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.suppressFocusOpen = false;

        this.onTriggerEnter = this.openFromEvent.bind(this);
        this.onTriggerClick = this.handleTriggerClick.bind(this);
        /* The pointer never closes a panel that holds keyboard focus, or focus would be lost. */
        this.onCloserEnter = (event) => {
          if (event.type === 'mouseenter' && this.panelHasFocus()) return;
          this.closePanels();
        };
        this.onLeave = () => {
          if (!this.panelHasFocus()) this.closePanels();
        };
        this.onFocusOut = this.handleFocusOut.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.onPanelClick = this.handlePanelClick.bind(this);
        this.onPageShow = () => {
          this.closePanels();
          this.closeDrawer(false);
        };
        this.onToggle = () => this.openDrawer();
        this.onDrawerClose = () => this.closeDrawer(true);
        this.onAccordion = this.toggleAccordion.bind(this);
        this.onBreakpoint = () => {
          if (this.desktop.matches) this.closeDrawer(false);
        };

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('mouseenter', this.onTriggerEnter);
          trigger.addEventListener('focus', this.onTriggerEnter);
          trigger.addEventListener('click', this.onTriggerClick);
        });
        this.closers.forEach((closer) => {
          closer.addEventListener('mouseenter', this.onCloserEnter);
          closer.addEventListener('focusin', this.onCloserEnter);
        });
        this.panels.forEach((panel) => panel.addEventListener('click', this.onPanelClick));
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('keydown', this.onKeydown);
        window.addEventListener('pageshow', this.onPageShow);
        if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
        this.drawerClosers.forEach((closer) => closer.addEventListener('click', this.onDrawerClose));
        this.accordions.forEach((button) => button.addEventListener('click', this.onAccordion));
        this.desktop.addEventListener('change', this.onBreakpoint);

        this.bindCartDrawer();
      }

      disconnectedCallback() {
        this.triggers.forEach((trigger) => {
          trigger.removeEventListener('mouseenter', this.onTriggerEnter);
          trigger.removeEventListener('focus', this.onTriggerEnter);
          trigger.removeEventListener('click', this.onTriggerClick);
        });
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.closers.forEach((closer) => {
          closer.removeEventListener('mouseenter', this.onCloserEnter);
          closer.removeEventListener('focusin', this.onCloserEnter);
        });
        this.panels.forEach((panel) => panel.removeEventListener('click', this.onPanelClick));
        this.removeEventListener('mouseleave', this.onLeave);
        this.removeEventListener('focusout', this.onFocusOut);
        this.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
        this.drawerClosers.forEach((closer) => closer.removeEventListener('click', this.onDrawerClose));
        this.accordions.forEach((button) => button.removeEventListener('click', this.onAccordion));
        this.desktop.removeEventListener('change', this.onBreakpoint);
        window.clearTimeout(this.drawerTimer);
        this.closeDrawer(false);
      }

      /* The theme cart drawer binds #cart-icon-bubble itself. If its script ran
         before this header existed, ask it to bind again. */
      bindCartDrawer() {
        const cartLink = this.querySelector('#cart-icon-bubble');
        if (!cartLink) return;
        const attach = () => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || cartLink.getAttribute('aria-haspopup') === 'dialog') return;
          if (typeof drawer.setHeaderCartIconAccessibility === 'function') {
            drawer.setHeaderCartIconAccessibility();
          }
        };
        if (customElements.get('cart-drawer')) {
          attach();
        } else {
          customElements.whenDefined('cart-drawer').then(attach);
        }
      }

      /* ---------- Mega menu ---------- */

      openFromEvent(event) {
        if (!this.desktop.matches) return;
        if (event.type === 'focus' && this.suppressFocusOpen) {
          this.suppressFocusOpen = false;
          return;
        }
        const trigger = event.currentTarget;
        if (event.type === 'mouseenter' && this.panelHasFocus() && this.openTrigger() !== trigger) return;
        this.openPanel(trigger.dataset.openPanel, trigger);
      }

      /* A trigger that is a link goes to its page on click. One that is a button (a menu item
         linked to #) is a disclosure button. From the keyboard (Enter or Space, which give a
         click with detail 0) it toggles its panel. A mouse click or a tap only opens it: hover
         or the tap's own emulated mouseenter has already opened the panel by then, so a toggle
         would shut it at once. Escape and moving away still close it. */
      handleTriggerClick(event) {
        const trigger = event.currentTarget;
        if (trigger.tagName !== 'BUTTON' || !this.desktop.matches) return;
        const fromKeyboard = event.detail === 0;
        if (fromKeyboard && trigger.getAttribute('aria-expanded') === 'true') {
          this.closePanels();
        } else {
          this.openPanel(trigger.dataset.openPanel, trigger);
        }
      }

      openPanel(name, trigger) {
        this.closeSearch();
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== name;
        });
        this.triggers.forEach((item) => {
          item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
        });
        /* Escape must close a panel opened by hover even while focus is elsewhere on the page. */
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      closePanels() {
        this.panels.forEach((panel) => {
          panel.hidden = true;
        });
        this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      openTrigger() {
        return this.triggers.find((item) => item.getAttribute('aria-expanded') === 'true');
      }

      panelHasFocus() {
        const panel = this.panels.find((item) => !item.hidden);
        return Boolean(panel && panel.contains(document.activeElement));
      }

      handlePanelClick(event) {
        if (event.target.closest('a')) this.closePanels();
      }

      handleFocusOut(event) {
        if (!this.contains(event.relatedTarget)) this.closePanels();
      }

      handleKeydown(event) {
        if (event.key !== 'Escape') return;
        if (this.drawer && !this.drawer.hidden) this.closeDrawer(true);
      }

      /* Listens on the document only while a panel is open. Focus moves back to the trigger only
         when it was inside the panel being hidden, and that focus must not reopen the panel. */
      handleDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        const trigger = this.openTrigger();
        if (!trigger) return;
        const focusInPanel = this.panelHasFocus();
        this.closePanels();
        if (focusInPanel) {
          this.suppressFocusOpen = true;
          trigger.focus();
        }
      }

      /* Opening a mega menu closes the theme's search overlays so they never stack. */
      closeSearch() {
        document.querySelectorAll('details-modal, predictive-search').forEach((node) => {
          if (typeof node.close === 'function') {
            try {
              node.close(false);
            } catch (error) {
              node.removeAttribute('open');
            }
          }
          node.removeAttribute('open');
        });
      }

      /* ---------- Mobile menu drawer ---------- */

      openDrawer() {
        if (!this.drawer || !this.sheet) return;
        window.clearTimeout(this.drawerTimer);
        this.drawer.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => {
          this.drawer.classList.add('is-open');
          const first = this.sheet.querySelector('[data-drawer-close]') || this.sheet;
          if (typeof window.trapFocus === 'function') {
            window.trapFocus(this.sheet, first);
          } else {
            first.focus();
          }
        });
      }

      closeDrawer(returnFocus) {
        if (!this.drawer || this.drawer.hidden) return;
        this.drawer.classList.remove('is-open');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        if (typeof window.removeTrapFocus === 'function') {
          window.removeTrapFocus(returnFocus ? this.toggle : null);
        } else if (returnFocus && this.toggle) {
          this.toggle.focus();
        }
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.drawerTimer = window.setTimeout(() => {
          this.drawer.hidden = true;
        }, reduced ? 0 : 300);
      }

      toggleAccordion(event) {
        const button = event.currentTarget;
        const region = document.getElementById(button.getAttribute('aria-controls'));
        if (!region) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        region.hidden = !open;
      }
    }
  );
}
